import { useState, useCallback, useEffect } from "react";
import { DateInput, Button, OptionGroup, DataTable } from "@/components";
import { RightWrapper } from "../../../components";

import ColumnHelper from "@/utils/ColumnHelper";
import { useMemo } from "react";
import { useFilteredData, useApi } from "@/";
import { getCreditNoteData } from "../../../utils/Data";
import { HiOutlineDownload } from "react-icons/hi";
import { BsFiletypeXls } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { subDays } from "date-fns";
import {
  fetchCreditNotes,
  selectAllCodRemittances,
  selectAllCreditNotes,
} from "../../../app/slices";
const CreditNote = () => {
  const currentDate = new Date();
  const dateRange = useSelector((state) => state.app?.appData?.dateRange) || {};
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;
  const options = [
    { label: "Bulk CN", value: "bulk_cn", icon: <BsFiletypeXls /> },
  ];
  const [filters, setFilters] = useState({
    date: { from: startOfMonthDate, to: endOfMonthDate },
  });
  const handleOptionClick = (value) => {
    console.log("Option Selected:", value);
    if (value === "bulk_cn") {
      handleDownload(selectedRows.map((row) => row._id));
    }
  };
  const [selectedRows, setSelectedRows] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);
  const { callApi } = useApi();
  const dispatch = useDispatch();

  const fetchData = useCallback(async () => {
    try {
      await dispatch(
        fetchCreditNotes({
          startDate: startOfMonthDate,
          endDate: endOfMonthDate,
        })
      );
    } catch (error) {
      console.error("Error fetching credit notes:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const creditNotes = useSelector(selectAllCreditNotes) || [];
  console.log("CreditNotes>>>>>>>", creditNotes);
  // Ensure rawData is only generated once
  const rawData = useMemo(() => getCreditNoteData(100), []);

  // Filter configuration
  const filterConfig = useMemo(
    () => ({
      date: { type: "date", itemKey: "creditNoteDate" },
    }),
    []
  );

  // Memoize filtered data to prevent unnecessary re-renders
  const { data: finalFilteredData, isLoading } = useFilteredData(
    creditNotes,
    appliedFilters,
    filterConfig
  );
  const handleDownload = async (ids) => {
    const loadingIndicator = document.createElement("div");
    loadingIndicator.textContent = "Downloading invoice...";
    loadingIndicator.style.position = "fixed";
    loadingIndicator.style.bottom = "20px";
    loadingIndicator.style.right = "20px";
    loadingIndicator.style.backgroundColor = "#4CAF50";
    loadingIndicator.style.color = "white";
    loadingIndicator.style.padding = "10px";
    loadingIndicator.style.borderRadius = "5px";
    loadingIndicator.style.zIndex = "1000";
    document.body.appendChild(loadingIndicator);
    try {
      console.log("Downloading invoice:", ids);

      // Add loading indicator

      // Use the existing callApi hook with blob response type
      const response = await callApi(
        "/user/downloadCreditNote",
        "POST",
        { ids },
        null,
        {
          Accept: "application/pdf",
        },
        "blob"
      );

      // Check if response is valid
      if (!response || response.size === 0) {
        console.error("Empty response received");
        document.body.removeChild(loadingIndicator);
        return;
      }

      // Create blob with proper MIME type
      const blob = new Blob([response], { type: "application/pdf" });

      // Create object URL
      const url = window.URL.createObjectURL(blob);

      // Create and trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = `CreditNote-${new Date().getTime()}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup with appropriate timeout
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        document.body.removeChild(loadingIndicator);
      }, 2000);
    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to download Credit Note. Please try again.");
      document.body.removeChild(loadingIndicator);
    }
  };
  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);
  const formatLongDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: undefined,
    });
  }, []);
  const formatCreditNoteNumber = useCallback((number) => {
    if (!number) return 'N/A';
    // Assuming the input format is CN25050001
    const matches = number.match(/CN(\d{2})(\d{2})(\d{4})/);
    if (!matches) return number;
    
    return `ICC/CN/${matches[1]}/${matches[2]}/${matches[3]}`;
  }, []);

  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Credit ID",
        accessorKey: "creditNoteNumber",
        cell: (info) => formatCreditNoteNumber(info.row.original.creditNoteNumber),
        meta: {
          tooltip: (row) => formatCreditNoteNumber(row.creditNoteNumber),
        },
      }),
      ColumnHelper({
        header: "Credit Date",
        accessorKey: "",
        cell: (info) => formatDate(info.row.original.creditNoteDate),
        meta: {
          tooltip: (row) => formatDate(row.creditNoteDate),
        },
      }),
      ColumnHelper({
        header: "Credit Period",
        accessorKey: "",
        cell: (info) => formatLongDate(info.row.original.creditNoteDate),
        meta: {
          tooltip: (row) => formatLongDate(row.creditNoteDate),
        },
      }),
      ColumnHelper({
        header: "Credit Amount",
        accessorKey: "totalAmount",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.totalAmount)}
          </span>
        ),
        meta: {
          tooltip: (row) => formatCurrency(row.totalAmount),
        },
      }),
      ColumnHelper({
        header: "Download",
        accessorKey: "_id",
        cell: (info) => (
          <div className="flex gap-1 justify-start items-center">
            <Button
              size="sm"
              className="!px-1.5 !py-1 !rounded-md "
              icon={HiOutlineDownload}
              onClick={() => handleDownload([info.row.original._id])}
            />
          </div>
        ),
        meta: {
          tooltip: () => "Download Credit Note",
        },
      }),
    ],
    []
  );

  // Handle filter changes
  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDateChange = useCallback((dateRange) => {
    setFilters((prev) => ({
      ...prev,
      date: { from: dateRange?.from || "", to: dateRange?.to || "" },
    }));
  }, []);

  // Apply filters without resetting raw data
  const handleApply = useCallback(() => {
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  // Reset filters properly
  const handleReset = useCallback(() => {
    const initialFilters = {
      date: { from: "", to: "" },
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, []);
  return (
    <RightWrapper>
      <div className="flex flex-col gap-4 mt-2 px-2 relative pb-20 md:pb-4">
        {/* Filters Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <DateInput
            label="Date Filter"
            placeholder="Select From & To date"
            mode="range"
            value={filters.date}
            onChange={(value) => handleChange("date", value)}
          />
          <div className="flex gap-2 justify-end sm:justify-start">
            <Button variant="default" size="md" onClick={handleApply}>
              Apply
            </Button>
            <Button variant="outline" size="md" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>

        {/* Option Group Section */}
        {selectedRows.length > 0 && (
          <OptionGroup
            options={options}
            selectedCount={selectedRows.length}
            onClick={handleOptionClick}
          />
        )}
        <DataTable
          columns={columns}
          tittle="Credit Note"
          data={finalFilteredData}
          getSelectedRows={setSelectedRows}
          isLoading={isLoading}
          resetPage={resetPageCounter}
        />
      </div>
    </RightWrapper>
  );
};

export default CreditNote;
